import { Subject } from 'rxjs';
import { GameServices } from '../../services/game-services';
import { CardViewModel } from '../cards/card-view-model';
import { Card, Croupier, PileId } from 'game-logic';
import { delay } from 'src/games/common/helper';
import { UxMetrics } from '../../model/ux-metrics';

export class CroupierCardStackViewModel {
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get logicEventQueue$() {
    return this.gameServices.blackjackGameRunner.logicEventQueue$;
  }
  public get cardViewModelChanges$() {
    return this.cardViewModelChanges.asObservable();
  }
  public get peekAtCard$() {
    return this.peekAtCard.asObservable();
  }

  private cardViewModels: CardViewModel[] = [];
  private readonly cardViewModelChanges = new Subject<{
    type: 'addopen' | 'addhidden' | 'flip' | 'remove';
    viewModel: CardViewModel;
    delay: number;
  }>();
  private readonly peekAtCard = new Subject<{ cardIndex: number; delay: number; pause: number }>();

  constructor(private gameServices: GameServices) {}

  public resetGameScene() {
    this.clearAllCroupierCards();
  }

  public async drawDeckCroupier(
    croupier: Croupier,
    cardIndex: number | undefined,
    openCard: boolean,
  ): Promise<void> {
    for (const [itemIndex, card] of croupier.allFaceAndHiddenCards.entries()) {
      if (card.index === cardIndex) {
        await this.stackCard(card, itemIndex, openCard);
      }
    }
  }

  public async flipCroupierCard(croupier: Croupier, cardIndex: number | undefined): Promise<void> {
    const cardViewModel = this.cardViewModels.find((vm) => vm.cardIndex === cardIndex);
    if (cardViewModel) {
      const delayMs = UxMetrics.CardRevealDelay;

      cardViewModel.refresh();
      this.cardViewModelChanges.next({
        type: 'flip',
        viewModel: cardViewModel,
        delay: delayMs,
      });

      await delay(delayMs);
    }
  }

  public async clearAllCroupierCards(): Promise<void> {
    if (this.cardViewModels.length === 0) {
      return;
    }

    const delayMs = UxMetrics.CardClearDelay / this.cardViewModels.length;
    for (const cardViewModel of this.cardViewModels.slice().reverse()) {
      this.cardViewModelChanges.next({
        type: 'remove',
        viewModel: cardViewModel,
        delay: UxMetrics.CardClearDelay,
      });
      await delay(delayMs);
    }
    this.cardViewModels = []; // Clear the array
  }

  public async peekBlackjack(croupier: Croupier): Promise<void> {
    const hiddenCards = croupier.allHiddenCards;
    if (hiddenCards.length !== 1) {
      return;
    }
    this.peekAtCard.next({
      cardIndex: hiddenCards[0].index,
      delay: UxMetrics.CroupierPeekDelay,
      pause: UxMetrics.CroupierPeekPause,
    });
    await delay(UxMetrics.CroupierPeekDelay + UxMetrics.CroupierPeekPause);
  }

  private async stackCard(card: Card, index: number, openCard: boolean): Promise<void> {
    const cardViewModel = new CardViewModel(this.gameServices, card, index, PileId.Croupier);
    cardViewModel.refresh();

    const delayMs = UxMetrics.CardOpenDelay;
    this.cardViewModels.push(cardViewModel);
    this.cardViewModelChanges.next({
      type: openCard ? 'addopen' : 'addhidden',
      viewModel: cardViewModel,
      delay: delayMs,
    });
    await delay(delayMs);
  }
}
